import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  ButtonGroup,
  IconButton,
  Button,
  MenuItem,
  FormControl,
  FormControlLabel,
  Select,
  Typography,
  Checkbox,
  FormGroup,
  Dialog,
  Badge,
} from '@material-ui/core';
import {
  CurrencyCircleDollar,
  Bed,
  GridFour,
  House,
  PaintBrushBroad,
  SlidersHorizontal,
  CaretDown,
  CaretUp,
} from 'phosphor-react';
import _ from 'lodash';
import EventEmitter from '../../../../lib/eventEmitter';
import { UpdateFilterDialog } from '../../../../actions/actionTypes';
import { showSignupModal } from '../../../../actions/auth';
import {
  fetchFeedSearch,
  fetchMapFeed,
  saveFeedSearch,
  updateFilterSearch,
} from '../../../../actions/feed';
import {
  localStorageTracker,
  promptHelper,
  formatShortPrice,
} from '../../../../lib/helpers';
import { storageKeys } from '../../../../lib/storage';
import {
  searchFilterState,
  sqFeetRange,
  priceRange,
  hoaRange,
  yearRange,
} from '../constants';
import {
  IncrementButtonIcon,
  DecrementButtonIcon,
} from '../../../utility/Icons';
import styles from './styles';
import { get } from 'lodash';
import { CustomKeyboardArrowDown } from '../../../utility/SelectInputComponent';
import SwitchComponent from '../../../utility/SwitchComponent';
import SelectInputComponent from '../../../utility/SelectInputComponent';
import { ROOM_TYPES } from '../Filters/RoomCondition';
import { isWeb } from '../../../../lib/getDeviceSize';

const PriceRangeFilters = dynamic(() => import('../Filters/PriceRangeFilters'));
const BedAndBathFilters = dynamic(() => import('../Filters/BedAndBathFilters'));
const SquareFeetFilters = dynamic(() => import('../Filters/SquareFeetFilters'));
const HomeTypeFilters = dynamic(() => import('../Filters/HomeTypeFilters'));
const RoomConditionFilters = dynamic(() =>
  import('../Filters/RoomConditionFilters'),
);
const MoreFilters = dynamic(() => import('../Filters/MoreFilters'));
const RoomPhotoOrderFilter = dynamic(() => import('../Sorting/SortByPhoto'));

const DEFAULT_MAX_PRICE = 1000000000;
const DEFAULT_MAX_SQ_FT = 50000;

export interface Props {
  dispatch?: any;
  deviceHeight: number;
  feedSearch: any;
  fetchFeedSearch: Function;
  fetchMapFeed: Function;
  mobile: boolean;
  router: any;
  saveFeedSearch: Function;
  showSignupModal: Function;
  token: string;
  usePlaceBoundary: boolean;
  filters: any;
  filterBedBathOpen: boolean;
  filterHomeTypeOpen: boolean;
  filterPriceRangeOpen: boolean;
  filterRoomConditionOpen: boolean;
  filterRoomPhotoOrderOpen: boolean;
  filterSquareFeetOpen: boolean;
  filterMoreFiltersOpen: boolean;
  updateFilter: Function;
  updateFilterDialog: Function;
  signupSurveyDetails: any;
}

export const FilterForm: React.FC<Props> = props => {
  const ref = useRef<any>();
  const classes = styles(props);
  const router = useRouter();
  const [initialLoad, setInitialLoad] = useState(true);
  const [moreFilterCount, setMoreFilterCount] = useState(0);
  const {
    mobile,
    filters,
    feedSearch,
    updateFilter,
    updateFilterDialog,
    filterPriceRangeOpen,
    filterBedBathOpen,
    filterHomeTypeOpen,
    filterSquareFeetOpen,
    filterRoomConditionOpen,
    filterRoomPhotoOrderOpen,
    filterMoreFiltersOpen,
  } = props;

  const handleClick = (e: any) => {
    if (e.target.id === 'More Filters') {
      return;
    }
    if (
      ref.current &&
      !ref.current.contains(e.target) &&
      e.target.tagName !== 'LI'
    ) {
      toggleFilterDialog('');
    }
  };

  useEffect(() => {
    priceRangeFilter();
  }, [filters.minPrice, filters.maxPrice]);

  useEffect(() => {
    yearRangeFilter();
  }, [filters.maxAge]);

  useEffect(() => {
    modifySqFeetFilter();
  }, [filters.minSf, filters.maxSf]);

  useEffect(() => {
    handleMoreFilterCount(feedSearch);
  }, [
    feedSearch.daysOnMarket,
    feedSearch.maxHOA,
    feedSearch.minAge,
    feedSearch.maxAge,
    feedSearch.minParking,
    feedSearch.openHousesOnly,
    feedSearch.hasGarageSpaces,
  ]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);
    if (initialLoad) {
      const filters = JSON.parse(get(router, 'query.filters', '{}'));
      if (Object.keys(filters).length > 0) {
        preFillFilters(filters);
      } else {
        preFillFilters(props.feedSearch);
      }
    }
    // Set initalLoad to false after all the filters are filled
    // Used timeout to fix the flashing bug
    setTimeout(() => {
      setInitialLoad(false);
    }, 0);
    return () => {
      document.removeEventListener('mousedown', handleClick, false);
    };
  }, []);

  const toggleFilterDialog = (filterDialog: string) => {
    updateFilterDialog(filterDialog);
  };

  const preFillFilters = (feedSearch: any) => {
    const fetchRoomConditons = (feedSearch: any) => {
      if (feedSearch.conditionsByRoomType) {
        if (_.isString(feedSearch.conditionsByRoomType)) {
          return JSON.parse(feedSearch.conditionsByRoomType);
        } else if (_.isArray(feedSearch.conditionsByRoomType)) {
          return feedSearch.conditionsByRoomType;
        }
      }
      return [];
    };
    if (feedSearch) {
      updateFilter({
        condo:
          feedSearch.propType &&
          (feedSearch.propType.includes('Condominium/Co-Op') ||
            feedSearch.propType.includes('Condo'))
            ? true
            : false,
        minDaysOnMarket: feedSearch.minDaysOnMarket
          ? feedSearch.minDaysOnMarket
          : searchFilterState.minDaysOnMarket,
        daysOnMarket: feedSearch.daysOnMarket
          ? feedSearch.daysOnMarket
          : searchFilterState.daysOnMarket,
        maxAge: feedSearch.maxAge
          ? feedSearch.maxAge
          : searchFilterState.maxAge,
        maxHOA: feedSearch.maxHOA
          ? feedSearch.maxHOA
          : searchFilterState.maxHOA,
        maxPrice: feedSearch.maxPrice
          ? feedSearch.maxPrice
          : searchFilterState.maxPrice,
        minAge: feedSearch.minAge
          ? feedSearch.minAge
          : searchFilterState.minAge,
        minBath: feedSearch.minBath
          ? feedSearch.minBath
          : searchFilterState.minBath,
        minBr: feedSearch.minBr ? feedSearch.minBr : searchFilterState.minBr,
        minParking: feedSearch.minParking
          ? feedSearch.minParking
          : searchFilterState.minParking,
        minPrice: feedSearch.minPrice ? feedSearch.minPrice : 0,
        minSf: feedSearch.minSf ? feedSearch.minSf : searchFilterState.minSf,
        maxSf: feedSearch.maxSf ? feedSearch.maxSf : searchFilterState.maxSf,
        multi:
          feedSearch.propType && feedSearch.propType.includes('Multi-Family')
            ? true
            : false,
        openHousesOnly: feedSearch.openHousesOnly
          ? feedSearch.openHousesOnly
          : false,
        hasGarageSpaces: feedSearch.hasGarageSpaces
          ? feedSearch.hasGarageSpaces
          : false,
        single:
          feedSearch.propType &&
          (feedSearch.propType.includes('Single Family') ||
            feedSearch.propType.includes('Single-Family'))
            ? true
            : false,
        photosByRoom: feedSearch.photosByRoom
          ? feedSearch.photosByRoom
          : searchFilterState.photosByRoom,
        conditionsByRoomType: fetchRoomConditons(feedSearch),
      });
    }
  };

  // Modify maxPrice list based on the selected minPrice value
  const priceRangeFilter = () => {
    const index = priceRange.indexOf(filters.minPrice);
    const filteredPriceRange = priceRange.slice(index + 1);
    if (filteredPriceRange.indexOf(filters.maxPrice) < 0) {
      updateFilter({
        maxPrice: searchFilterState.maxPrice,
        priceRange: filteredPriceRange,
      });
    } else {
      updateFilter({ priceRange: filteredPriceRange });
    }
  };

  // Modify maxSf list based on the selected minSf value
  const modifySqFeetFilter = () => {
    const index = sqFeetRange.indexOf(filters.minSf);
    const filteredSqFeetRange = sqFeetRange.slice(index + 1);
    if (filteredSqFeetRange.indexOf(filters.maxSf) < 0) {
      updateFilter({
        maxSf: searchFilterState.maxSf,
        sqFeetRange: filteredSqFeetRange,
      });
    } else {
      updateFilter({ sqFeetRange: filteredSqFeetRange });
    }
  };

  // Modify minAge list based on the selected maxAge value
  const yearRangeFilter = () => {
    const index = yearRange.indexOf(filters.maxAge);
    const filteredYearRange = yearRange.slice(0, index);
    if (filteredYearRange.indexOf(filters.minAge) < 0) {
      updateFilter({
        minAge: searchFilterState.minAge,
        yearRange: filteredYearRange,
      });
    } else {
      updateFilter({ yearRange: filteredYearRange });
    }
  };

  const toggleDropDown = (filter: string) => {
    updateFilterDialog(filter);
  };

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const eventName = event.target.name as string;

    if (eventName === 'daysOnMarket') {
      const split =
        typeof event.target.value === 'string'
          ? event.target.value.split('-')
          : [];
      updateFilter({
        ['minDaysOnMarket']: parseInt(split[0]),
        ['daysOnMarket']: split.length > 1 ? parseInt(split[1]) : 100000,
      });
    } else {
      updateFilter({
        [eventName]: event.target.value,
      });
    }
  };

  const handleBedroomChange = (event: any, value: String) => {
    if (value === 'inc') {
      updateFilter({
        minBr: filters.minBr + 1,
      });
    } else if (value === 'dec') {
      updateFilter({
        minBr: filters.minBr === 0 ? 0 : filters.minBr - 1,
      });
    }
  };

  const handleParkingChange = (event: any, value: String) => {
    if (value === 'inc') {
      updateFilter({
        minParking: filters.minParking + 1,
      });
    } else if (value === 'dec') {
      updateFilter({
        minParking: filters.minParking === 0 ? 0 : filters.minParking - 1,
      });
    }
  };

  const handleBathroomChange = (event: any, value: String) => {
    if (value === 'inc') {
      updateFilter({
        minBath: filters.minBath + 1,
      });
    } else if (value === 'dec') {
      updateFilter({
        minBath: filters.minBath === 0 ? 0 : filters.minBath - 1,
      });
    }
  };

  const handleToggle = (name: any) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateFilter({ [name]: event.target.checked });
  };

  const handleMoreFilterCount = (feedSearch: any) => {
    let moreFilterCount = 0;
    const {
      minDaysOnMarket,
      daysOnMarket,
      maxHOA,
      minAge,
      maxAge,
      minParking,
      openHousesOnly,
      hasGarageSpaces,
    } = feedSearch;

    if (daysOnMarket && daysOnMarket !== searchFilterState.daysOnMarket) {
      moreFilterCount = moreFilterCount + 1;
    }
    if (maxHOA && maxHOA !== searchFilterState.maxHOA) {
      moreFilterCount = moreFilterCount + 1;
    }
    if (minAge && minAge !== searchFilterState.minAge) {
      moreFilterCount = moreFilterCount + 1;
    }
    if (maxAge && maxAge !== searchFilterState.maxAge) {
      moreFilterCount = moreFilterCount + 1;
    }
    if (minParking && minParking !== searchFilterState.minParking) {
      moreFilterCount = moreFilterCount + 1;
    }
    if (openHousesOnly && openHousesOnly !== searchFilterState.openHousesOnly) {
      moreFilterCount = moreFilterCount + 1;
    }
    if (
      hasGarageSpaces &&
      hasGarageSpaces !== searchFilterState.hasGarageSpaces
    ) {
      moreFilterCount = moreFilterCount + 1;
    }

    setMoreFilterCount(moreFilterCount);
  };

  const handleSubmit = (stateCopy: any) => {
    const values: any = Object.assign({}, stateCopy);
    values.propType = [];
    values.location = props.feedSearch.location;
    values.boundingBox = props.feedSearch.boundingBox;
    values.polygonBoundaries = props.feedSearch.polygonBoundaries;
    values.single &&
      values.propType.indexOf('Single-Family') < 0 &&
      values.propType.push('Single-Family');
    values.multi &&
      values.propType.indexOf('Multi-Family') < 0 &&
      values.propType.push('Multi-Family');
    values.condo &&
      values.propType.indexOf('Condo') < 0 &&
      values.propType.push('Condo');
    if (props.feedSearch.locationName) {
      values.locationName = props.feedSearch.locationName;
    }
    if (values.minAge === 0) {
      delete values.minAge;
    }
    if (values.maxAge === 0) {
      delete values.maxAge;
    }
    if (values.minPrice === 0) {
      delete values.minPrice;
    }
    if (values.maxPrice === DEFAULT_MAX_PRICE) {
      delete values.maxPrice;
    }
    if (values.minSf === 0) {
      delete values.minSf;
    }
    if (values.maxSf === DEFAULT_MAX_SQ_FT) {
      delete values.maxSf;
    }
    if (values.conditionsByRoomType) {
      values.conditionsByRoomType = JSON.stringify(values.conditionsByRoomType);
    }
    // Remove unnecessary properties so that only required values can be stored in DB
    delete values.priceRange;
    delete values.yearRange;
    delete values.single;
    delete values.multi;
    delete values.condo;

    if (!props.token) {
      localStorageTracker(
        storageKeys.noOfSearches,
        3,
        () => {
          props.fetchMapFeed(
            values.location,
            props.token,
            values.boundingBox,
            true,
            values,
            props.usePlaceBoundary,
          );
        },
        () => {
          promptHelper(
            storageKeys.homeVisitDate,
            props.showSignupModal('/search'),
          );
        },
      );
    } else {
      props.saveFeedSearch(values, props.token);
      props.fetchMapFeed(
        values.location,
        props.token,
        values.boundingBox,
        true,
        values,
        props.usePlaceBoundary,
      );
    }
  };

  const handleReset = (resetFields: any) => {
    EventEmitter.emit('onResetPress');
    const updatedFilters = {
      ...filters,
      ...resetFields,
    };
    updateFilter(updatedFilters);
    handleSubmit(updatedFilters);
  };

  const handleRoomFilter = (value: string) => {
    if (value !== filters.photosByRoom) {
      updateFilter({
        photosByRoom: value,
      });
    }
  };

  const toggleSortOption = (sortType: string) => {
    updateFilterDialog(sortType);
  };

  const handleRoomQualityFilter = (conditionsByRoomType: any) => {
    if (
      JSON.stringify(conditionsByRoomType) !==
      JSON.stringify(filters.conditionsByRoomType)
    ) {
      updateFilter({
        conditionsByRoomType,
      });
    }
  };

  const bedroomsFilter = () => {
    return (
      <div className={classes.bedroomsFilterContainer}>
        <Typography variant="body1" className={classes.bedroomsFilterTitle}>
          Bedrooms
        </Typography>
        <ButtonGroup
          size="small"
          className={classes.buttonGroup}
          aria-label="bedrooms button group"
        >
          <IconButton onClick={event => handleBedroomChange(event, 'dec')}>
            <DecrementButtonIcon />
          </IconButton>
          <span className={classes.counterText}>
            {filters.minBr ? filters.minBr : 'Any'}
          </span>
          <IconButton onClick={event => handleBedroomChange(event, 'inc')}>
            <IncrementButtonIcon />
          </IconButton>
        </ButtonGroup>
      </div>
    );
  };

  const bathroomsFilter = () => {
    return (
      <div className={classes.bathroomsFilterContainer}>
        <Typography variant="body1" className={classes.bathroomsFilterTitle}>
          Bathrooms
        </Typography>
        <ButtonGroup
          size="small"
          className={classes.buttonGroup}
          aria-label="bathroom button group"
        >
          <IconButton onClick={event => handleBathroomChange(event, 'dec')}>
            <DecrementButtonIcon />
          </IconButton>
          <span className={classes.counterText}>
            {filters.minBath ? filters.minBath : 'Any'}
          </span>
          <IconButton onClick={event => handleBathroomChange(event, 'inc')}>
            <IncrementButtonIcon />
          </IconButton>
        </ButtonGroup>
      </div>
    );
  };

  const parkingFilter = () => {
    return (
      <div className={classes.parkingFilterContainer}>
        <Typography variant="body1" className={classes.parkingFilterTitle}>
          Parking
        </Typography>
        <ButtonGroup
          size="small"
          className={classes.buttonGroup}
          aria-label="bathroom button group"
        >
          <IconButton onClick={event => handleParkingChange(event, 'dec')}>
            <DecrementButtonIcon />
          </IconButton>
          <span className={classes.counterText}>
            {filters.minParking ? filters.minParking : 'Any'}
          </span>
          <IconButton onClick={event => handleParkingChange(event, 'inc')}>
            <IncrementButtonIcon />
          </IconButton>
        </ButtonGroup>
      </div>
    );
  };

  const openHouseFilter = () => {
    return (
      <div className={classes.openHouseContainer}>
        <Typography variant="body1" className={classes.openHouseTitle}>
          Open Houses
        </Typography>
        <SwitchComponent
          name="openHouseSwitch"
          value="openHousesOnly"
          checked={filters.openHousesOnly}
          onChange={handleToggle('openHousesOnly')}
        />
      </div>
    );
  };

  const garageSpaceFilter = () => {
    return (
      <div className={classes.garageSpaceContainer}>
        <Typography variant="body1" className={classes.garageSpaceTitle}>
          Must Have Garage
        </Typography>
        <SwitchComponent
          name="garageSpaceSwitch"
          checked={filters.hasGarageSpaces}
          value="hasGarageSpaces"
          onChange={handleToggle('hasGarageSpaces')}
        />
      </div>
    );
  };

  const propertyFilter = () => {
    return (
      <div>
        <Typography variant="body1" className={classes.propertyFilterTitle}>
          Home Type
        </Typography>
        <FormGroup className={classes.propertyFilter}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.single}
                color="primary"
                onChange={handleToggle('single')}
                value="single"
                className={classes.propertyValues}
              />
            }
            label="Single-Family"
            className={classes.propertyLabel}
          />
          <FormControlLabel
            control={
              <Checkbox
                className={classes.propertyValues}
                checked={filters.condo}
                color="primary"
                onChange={handleToggle('condo')}
                value="condo"
              />
            }
            label="Condo/Townhouse"
            className={classes.propertyLabel}
          />
          <FormControlLabel
            control={
              <Checkbox
                className={classes.propertyValues}
                checked={filters.multi}
                color="primary"
                onChange={handleToggle('multi')}
                value="multi"
              />
            }
            label="Multi-Family"
            className={classes.propertyLabel}
          />
        </FormGroup>
      </div>
    );
  };

  const sqFeetFilter = () => {
    return (
      <form autoComplete="off">
        <div className={classes.filterFormContainer}>
          <FormControl className={classes.filterFormControl}>
            <Select
              value={filters.minSf}
              onChange={handleChange}
              inputProps={{
                id: 'minSf',
                name: 'minSf',
              }}
              input={<SelectInputComponent />}
              IconComponent={CustomKeyboardArrowDown}
            >
              <MenuItem
                className={
                  filters.minSf === 0
                    ? classes.selectedBox
                    : classes.unSelectedBox
                }
                value={0}
              >
                Min
              </MenuItem>
              {sqFeetRange.map((sqFeet: number, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    className={
                      filters.minSf === sqFeet
                        ? classes.selectedBox
                        : classes.unSelectedBox
                    }
                    value={sqFeet}
                  >
                    {sqFeet} sq ft
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <span className={classes.filterFormSeparator}>-</span>
          <FormControl className={classes.filterFormControl}>
            <Select
              value={filters.maxSf}
              onChange={handleChange}
              inputProps={{
                id: 'maxSf',
                name: 'maxSf',
              }}
              input={<SelectInputComponent />}
              IconComponent={CustomKeyboardArrowDown}
            >
              <MenuItem
                className={
                  filters.maxSf === DEFAULT_MAX_SQ_FT
                    ? classes.selectedBox
                    : classes.unSelectedBox
                }
                value={DEFAULT_MAX_SQ_FT}
              >
                Max
              </MenuItem>
              {filters.sqFeetRange.map((sqFeet: number, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    className={
                      filters.maxSf === sqFeet
                        ? classes.selectedBox
                        : classes.unSelectedBox
                    }
                    value={sqFeet}
                  >
                    {sqFeet} sq ft
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </form>
    );
  };

  const daysOnMarketFilter = () => {
    return (
      <div className={classes.daysOnMarketFilterContainer}>
        <Typography variant="body1" className={classes.daysOnMarketFilterTitle}>
          Days on Torii
        </Typography>
        <FormControl className={classes.daysOnMarketDropDown}>
          <Select
            value={`${filters.minDaysOnMarket}-${filters.daysOnMarket}`}
            onChange={handleChange}
            inputProps={{
              id: 'daysOnMarket',
              name: 'daysOnMarket',
            }}
            input={<SelectInputComponent />}
            IconComponent={CustomKeyboardArrowDown}
          >
            <MenuItem
              className={
                filters.daysOnMarket === 100000 && filters.minDaysOnMarket === 0
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={'0-100000'}
            >
              Any
            </MenuItem>
            <MenuItem
              className={
                filters.daysOnMarket === 1 && filters.minDaysOnMarket === 0
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={'0-1'}
            >
              {`<1 Day`}
            </MenuItem>
            <MenuItem
              className={
                filters.daysOnMarket === 7 && filters.minDaysOnMarket === 0
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={'0-7'}
            >
              {`<7 Days`}
            </MenuItem>
            <MenuItem
              className={
                filters.daysOnMarket === 30 && filters.minDaysOnMarket === 7
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={'7-30'}
            >
              {`7-30 Days`}
            </MenuItem>
            <MenuItem
              className={
                filters.daysOnMarket === 90 && filters.minDaysOnMarket === 30
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={'30-90'}
            >
              {`1-3 Months`}
            </MenuItem>
            <MenuItem
              className={
                filters.minDaysOnMarket === 90
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={'90-100000'}
            >
              {`>3 Months`}
            </MenuItem>
          </Select>
        </FormControl>
      </div>
    );
  };

  const maxHOAFilter = () => {
    return (
      <div className={classes.maxHOAFilterContainer}>
        <Typography variant="body1" className={classes.maxHOAFilterTitle}>
          Max HOA
        </Typography>
        <FormControl className={classes.maxHOADropDown}>
          <Select
            value={filters.maxHOA}
            onChange={handleChange}
            displayEmpty
            inputProps={{
              id: 'maxHOA',
              name: 'maxHOA',
            }}
            input={<SelectInputComponent />}
            IconComponent={CustomKeyboardArrowDown}
          >
            <MenuItem
              className={
                filters.maxHOA === 1000000
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={1000000}
            >
              Any
            </MenuItem>
            <MenuItem
              className={
                filters.maxHOA === 0
                  ? classes.selectedBox
                  : classes.unSelectedBox
              }
              value={0}
            >
              No HOA fee
            </MenuItem>
            {hoaRange.map((hoa: number, index: number) => {
              return (
                <MenuItem
                  key={index}
                  className={
                    filters.maxHOA === hoa
                      ? classes.selectedBox
                      : classes.unSelectedBox
                  }
                  value={hoa}
                >
                  ${hoa}/month
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
    );
  };

  const yearFilter = () => {
    return (
      <div className={classes.yearFilterContainer}>
        <Typography variant="body1" className={classes.yearFilterTitle}>
          Year Built
        </Typography>
        <div className={classes.yearFilterDropdown}>
          <FormControl className={classes.yearControl}>
            <Select
              value={filters.maxAge}
              onChange={handleChange}
              displayEmpty
              inputProps={{
                id: 'maxAge',
                name: 'maxAge',
              }}
              input={<SelectInputComponent />}
              IconComponent={CustomKeyboardArrowDown}
            >
              {yearRange.map((year: number, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    className={
                      filters.maxAge === year
                        ? classes.selectedBox
                        : classes.unSelectedBox
                    }
                    value={year}
                  >
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <span className={classes.yearFilterSeparator}>-</span>
          <FormControl className={classes.yearControl}>
            <Select
              value={filters.minAge}
              onChange={handleChange}
              displayEmpty
              inputProps={{
                id: 'minAge',
                name: 'minAge',
              }}
              input={<SelectInputComponent />}
              IconComponent={CustomKeyboardArrowDown}
            >
              {filters.yearRange.map((year: number, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    className={
                      filters.minAge === year
                        ? classes.selectedBox
                        : classes.unSelectedBox
                    }
                    value={year}
                  >
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </div>
    );
  };

  const priceFilterForFilterForm = () => {
    return (
      <form autoComplete="off">
        <Typography variant="body1" className={classes.priceFilterTitle}>
          Price Range
        </Typography>
        <div className={classes.filterFormContainer}>
          <FormControl className={classes.filterFormControl}>
            <Select
              value={filters.minPrice}
              onChange={handleChange}
              inputProps={{
                id: 'minPrice',
                name: 'minPrice',
              }}
              input={<SelectInputComponent />}
              IconComponent={CustomKeyboardArrowDown}
            >
              <MenuItem
                className={
                  filters.minPrice === 0
                    ? classes.selectedBox
                    : classes.unSelectedBox
                }
                value={0}
              >
                Min
              </MenuItem>
              {priceRange.map((price: number, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    className={
                      filters.minPrice === price
                        ? classes.selectedBox
                        : classes.unSelectedBox
                    }
                    value={price}
                  >
                    ${formatShortPrice(price)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <span className={classes.filterFormSeparator}>-</span>
          <FormControl className={classes.filterFormControl}>
            <Select
              value={filters.maxPrice}
              onChange={handleChange}
              inputProps={{
                id: 'maxPrice',
                name: 'maxPrice',
              }}
              input={<SelectInputComponent />}
              IconComponent={CustomKeyboardArrowDown}
            >
              <MenuItem
                className={
                  filters.maxPrice === DEFAULT_MAX_PRICE ||
                  filters.maxPrice === props.signupSurveyDetails.maxBudget
                    ? classes.selectedBox
                    : classes.unSelectedBox
                }
                value={DEFAULT_MAX_PRICE}
              >
                Max
              </MenuItem>
              {filters.priceRange.map((price: number, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    className={
                      filters.maxPrice === price
                        ? classes.selectedBox
                        : classes.unSelectedBox
                    }
                    value={price}
                  >
                    ${formatShortPrice(price)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </form>
    );
  };

  const renderPriceRangeFilter = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <PriceRangeFilters
          mobile={mobile}
          deviceHeight={props.deviceHeight}
          toggleDropDown={toggleDropDown}
          priceFilter={priceFilterForFilterForm}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          onClose={() => toggleFilterDialog('filterPriceRangeOpen')}
        />
      </div>
    );
  };

  const renderBedAndBathFilter = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <BedAndBathFilters
          mobile={mobile}
          deviceHeight={props.deviceHeight}
          toggleDropDown={toggleDropDown}
          bathroomsFilter={bathroomsFilter}
          bedroomsFilter={bedroomsFilter}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          onClose={() => toggleFilterDialog('filterBedBathOpen')}
        />
      </div>
    );
  };

  const renderSquareFeetFilter = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <SquareFeetFilters
          mobile={mobile}
          deviceHeight={props.deviceHeight}
          toggleDropDown={toggleDropDown}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          sqFeetFilter={sqFeetFilter}
          onClose={() => toggleFilterDialog('filterSquareFeetOpen')}
        />
      </div>
    );
  };

  const renderHomeTypeFilter = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <HomeTypeFilters
          mobile={mobile}
          deviceHeight={props.deviceHeight}
          toggleDropDown={toggleDropDown}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          propertyFilter={propertyFilter}
          onClose={() => toggleFilterDialog('filterHomeTypeOpen')}
        />
      </div>
    );
  };

  const renderRoomConditionFilter = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <RoomConditionFilters
          mobile={mobile}
          deviceHeight={props.deviceHeight}
          toggleDropDown={toggleDropDown}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          handleQualityFilter={handleRoomQualityFilter}
          defaultRoomQuality={filters.conditionsByRoomType}
          onClose={() => toggleFilterDialog('filterRoomConditionOpen')}
        />
      </div>
    );
  };

  const renderRoomPhotoOrderFilter = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <RoomPhotoOrderFilter
          defaultPhotosOrder={filters.photosByRoom}
          defaultRoomQuality={filters.conditionsByRoomType}
          handleRoomFilter={handleRoomFilter}
          handleQualityFilter={handleRoomQualityFilter}
          toggleSortOption={toggleSortOption}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          onClose={() => toggleFilterDialog('showRoomPhotoOrder')}
        />
      </div>
    );
  };

  const renderMoreFilters = () => {
    return (
      <div style={props.mobile ? { position: 'relative' } : {}}>
        <MoreFilters
          mobile={mobile}
          deviceHeight={props.deviceHeight}
          daysOnMarketFilter={daysOnMarketFilter}
          maxHOAFilter={maxHOAFilter}
          yearFilter={yearFilter}
          parkingFilter={parkingFilter}
          garageSpaceFilter={garageSpaceFilter}
          openHouseFilter={openHouseFilter}
          handleReset={handleReset}
          handleSubmit={() => handleSubmit(filters)}
          toggleDropDown={toggleDropDown}
          onClose={() => toggleFilterDialog('filterMoreFiltersOpen')}
        />
      </div>
    );
  };

  const photosByRoom: any[] =
    mobile && feedSearch.photosByRoom
      ? ROOM_TYPES.reduce((previousValue: any, currentValue: any) => {
          if (feedSearch.photosByRoom.includes(currentValue.rt)) {
            return previousValue.concat(currentValue.label);
          } else {
            return previousValue;
          }
        }, [])
      : [];

  return (
    <div className={classes.filtersContainer}>
      <div className="start-xs" style={{ height: '100%' }}>
        <div className={classes.root}>
          <div className={classes.rootContainer}>
            <div className={classes.filtersGroupOne}>
              {feedSearch.minPrice ||
              (feedSearch.maxPrice &&
                feedSearch.maxPrice !== searchFilterState.maxPrice) ? (
                <Button
                  id="Price Range"
                  className={classes.filterButtonActive}
                  onClick={() => toggleDropDown('filterPriceRangeOpen')}
                >
                  {!props.mobile && (
                    <CurrencyCircleDollar
                      size={24}
                      color="#463264"
                      weight="duotone"
                    />
                  )}
                  <span id="Price Range" className={classes.filterButtonText}>
                    {`${
                      feedSearch.minPrice
                        ? `$${formatShortPrice(feedSearch.minPrice)}`
                        : 'Min'
                    }`}{' '}
                    -{' '}
                    {`${
                      feedSearch.maxPrice
                        ? `$${formatShortPrice(feedSearch.maxPrice)}`
                        : 'Max'
                    }`}
                  </span>
                  {filterPriceRangeOpen ? (
                    <CaretUp
                      id="Price Range"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Price Range"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              ) : (
                <Button
                  id="Price Range"
                  className={classes.filterButton}
                  onClick={() => toggleDropDown('filterPriceRangeOpen')}
                >
                  {!props.mobile && (
                    <CurrencyCircleDollar
                      size={24}
                      color="#979797"
                      weight="duotone"
                    />
                  )}
                  <span id="Price Range" className={classes.filterButtonText}>
                    Price Range
                  </span>
                  {filterPriceRangeOpen ? (
                    <CaretUp
                      id="Price Range"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Price Range"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
              {feedSearch.minBr || feedSearch.minBath ? (
                <Button
                  id="Bed & Bath"
                  className={classes.filterButtonActive}
                  onClick={() => toggleDropDown('filterBedBathOpen')}
                >
                  {!props.mobile && (
                    <Bed size={24} color="#463264" weight="duotone" />
                  )}
                  <span id="Bed & Bath" className={classes.filterButtonText}>
                    {feedSearch.minBr > 0 && `${feedSearch.minBr}+ br`}{' '}
                    {feedSearch.minBath > 0 && `${feedSearch.minBath}+ ba`}
                  </span>
                  {filterBedBathOpen ? (
                    <CaretUp
                      id="Bed & Bath"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Bed & Bath"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              ) : (
                <Button
                  id="Bed & Bath"
                  className={classes.filterButton}
                  onClick={() => toggleDropDown('filterBedBathOpen')}
                >
                  {!props.mobile && (
                    <Bed size={24} color="#979797" weight="duotone" />
                  )}
                  <span id="Bed & Bath" className={classes.filterButtonText}>
                    Bed & Bath
                  </span>
                  {filterBedBathOpen ? (
                    <CaretUp
                      id="Bed & Bath"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Bed & Bath"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
              {feedSearch.minSf ||
              (feedSearch.maxSf &&
                feedSearch.maxSf !== searchFilterState.maxSf) ? (
                <Button
                  id="Square Feet"
                  className={classes.filterButtonActive}
                  onClick={() => toggleDropDown('filterSquareFeetOpen')}
                >
                  {!props.mobile && (
                    <GridFour size={24} color="#463264" weight="duotone" />
                  )}
                  <span id="Square Feet" className={classes.filterButtonText}>
                    {feedSearch.minSf ? `${feedSearch.minSf} sq ft` : 'Min'} -{' '}
                    {feedSearch.maxSf ? `${feedSearch.maxSf} sq ft` : 'Max'}
                  </span>
                  {filterSquareFeetOpen ? (
                    <CaretUp
                      id="Square Feet"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Square Feet"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              ) : (
                <Button
                  id="Square Feet"
                  className={classes.filterButton}
                  onClick={() => toggleDropDown('filterSquareFeetOpen')}
                >
                  {!props.mobile && (
                    <GridFour size={24} color="#979797" weight="duotone" />
                  )}
                  <span id="Square Feet" className={classes.filterButtonText}>
                    Square Feet
                  </span>
                  {filterSquareFeetOpen ? (
                    <CaretUp
                      id="Square Feet"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Square Feet"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
            </div>
            <div className={classes.filtersGroupTwo}>
              {feedSearch.propType && feedSearch.propType.length > 0 ? (
                <Button
                  id="Home Type"
                  className={classes.filterButtonActive}
                  onClick={() => toggleDropDown('filterHomeTypeOpen')}
                >
                  {!props.mobile && (
                    <House size={24} color="#463264" weight="duotone" />
                  )}
                  <span
                    id="Home Type"
                    className={classes.filterButtonText}
                    style={{ width: 180 }}
                  >
                    {feedSearch.propType.join(', ')}
                  </span>
                  {filterHomeTypeOpen ? (
                    <CaretUp
                      id="Home Type"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Home Type"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              ) : (
                <Button
                  id="Home Type"
                  className={classes.filterButton}
                  onClick={() => toggleDropDown('filterHomeTypeOpen')}
                >
                  {!props.mobile && (
                    <House size={24} color="#979797" weight="duotone" />
                  )}
                  <span id="Home Type" className={classes.filterButtonText}>
                    Home Type
                  </span>
                  {filterHomeTypeOpen ? (
                    <CaretUp
                      id="Home Type"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Home Type"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
              {feedSearch.conditionsByRoomType &&
              JSON.parse(feedSearch.conditionsByRoomType).length > 0 ? (
                <Button
                  id="Room Condition"
                  className={[
                    classes.filterButtonActive,
                    classes.filterButtonRoomCondition,
                  ].join(' ')}
                  onClick={() => toggleDropDown('filterRoomConditionOpen')}
                >
                  {!props.mobile && (
                    <PaintBrushBroad
                      size={24}
                      color="#463264"
                      weight="duotone"
                    />
                  )}
                  <span
                    id="Room Condition"
                    className={classes.filterButtonText}
                  >
                    Room Condition
                  </span>
                  <Badge
                    id="Room Condition"
                    className={classes.badgeIcon}
                    badgeContent={
                      JSON.parse(feedSearch.conditionsByRoomType).length
                    }
                    color="primary"
                  >
                    <React.Fragment />
                  </Badge>
                  {filterRoomConditionOpen ? (
                    <CaretUp
                      id="Room Condition"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Room Condition"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              ) : (
                <Button
                  id="Room Condition"
                  className={[
                    classes.filterButton,
                    classes.filterButtonRoomCondition,
                  ].join(' ')}
                  onClick={() => toggleDropDown('filterRoomConditionOpen')}
                >
                  {!props.mobile && (
                    <PaintBrushBroad
                      size={24}
                      color="#979797"
                      weight="duotone"
                    />
                  )}
                  <span
                    id="Room Condition"
                    className={classes.filterButtonText}
                    style={{ marginRight: 10 }}
                  >
                    Room Condition
                  </span>
                  {filterRoomConditionOpen ? (
                    <CaretUp
                      id="Room Condition"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Room Condition"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
              {mobile && (
                <Button
                  id="Room Photo Order"
                  className={[
                    classes.filterButton,
                    classes.filterButtonRoomCondition,
                  ].join(' ')}
                  onClick={() => toggleDropDown('filterRoomPhotoOrderOpen')}
                >
                  <span
                    id="Room Photo Order"
                    className={classes.filterButtonText}
                    style={{ marginRight: 10 }}
                  >
                    Room Photo Sort
                  </span>
                  {photosByRoom.length > 0 && (
                    <Badge
                      id="Room Photo Order"
                      className={classes.badgeIcon}
                      badgeContent={photosByRoom.length}
                      color="primary"
                    >
                      <React.Fragment />
                    </Badge>
                  )}
                  {filterRoomPhotoOrderOpen ? (
                    <CaretUp
                      id="Room Photo Order"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="Room Photo Order"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
              {moreFilterCount > 0 ? (
                <Button
                  id="More Filters"
                  className={classes.filterButtonActive}
                  style={{
                    margin: '0px',
                  }}
                  onClick={() => toggleDropDown('filterMoreFiltersOpen')}
                >
                  {!props.mobile && (
                    <SlidersHorizontal
                      size={24}
                      color="#463264"
                      weight="duotone"
                    />
                  )}
                  <span id="More Filters" className={classes.filterButtonText}>
                    More Filters
                  </span>
                  <Badge
                    id="More Filters"
                    className={classes.badgeIcon}
                    badgeContent={moreFilterCount}
                    color="primary"
                  >
                    <React.Fragment />
                  </Badge>
                  {filterMoreFiltersOpen ? (
                    <CaretUp
                      id="More Filters"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="More Filters"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              ) : (
                <Button
                  id="More Filters"
                  className={classes.filterButton}
                  style={{
                    marginLeft: isWeb() ? '16px' : '',
                  }}
                  onClick={() => toggleDropDown('filterMoreFiltersOpen')}
                >
                  {!props.mobile && (
                    <SlidersHorizontal
                      size={24}
                      color="#979797"
                      weight="duotone"
                    />
                  )}
                  <span id="More Filters" className={classes.filterButtonText}>
                    More Filters
                  </span>
                  {filterMoreFiltersOpen ? (
                    <CaretUp
                      id="More Filters"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  ) : (
                    <CaretDown
                      id="More Filters"
                      size={16}
                      color="#463264"
                      weight="bold"
                    />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {filterPriceRangeOpen && renderPriceRangeFilter()}
      {filterBedBathOpen && renderBedAndBathFilter()}
      {filterSquareFeetOpen && renderSquareFeetFilter()}
      {filterHomeTypeOpen && renderHomeTypeFilter()}
      {filterRoomConditionOpen && renderRoomConditionFilter()}
      {filterRoomPhotoOrderOpen && mobile && renderRoomPhotoOrderFilter()}
      {filterMoreFiltersOpen && renderMoreFilters()}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  feedSearch: state.feed.feedSearch,
  filters: state.feed.filterSearch,
  token: state.auth.token,
  usePlaceBoundary: state.feed.usePlaceBoundary,
  filterBedBathOpen: state.dialogs.filterBedBathOpen,
  filterHomeTypeOpen: state.dialogs.filterHomeTypeOpen,
  filterPriceRangeOpen: state.dialogs.filterPriceRangeOpen,
  filterRoomConditionOpen: state.dialogs.filterRoomConditionOpen,
  filterRoomPhotoOrderOpen: state.dialogs.filterRoomPhotoOrderOpen,
  filterSquareFeetOpen: state.dialogs.filterSquareFeetOpen,
  filterMoreFiltersOpen: state.dialogs.filterMoreFiltersOpen,
  signupSurveyDetails: state.signupSurvey.signupSurveyDetails,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchFeedSearch(token: string) {
    dispatch(fetchFeedSearch(token));
  },
  fetchMapFeed(
    mapCenter: string,
    token: string,
    boundingBox: string,
    useBoundingBox: boolean,
    feedSearch: any,
    usePlaceBoundary: boolean,
    fetchLargePhotoList: boolean,
    mapView: boolean,
  ) {
    dispatch(
      fetchMapFeed(
        mapCenter,
        token,
        boundingBox,
        useBoundingBox,
        feedSearch,
        usePlaceBoundary,
        fetchLargePhotoList,
        mapView,
      ),
    );
  },
  saveFeedSearch(values: any, token: string) {
    dispatch(saveFeedSearch(values, token));
  },
  showSignupModal(postAuthPath: string) {
    dispatch(showSignupModal(postAuthPath));
  },
  updateFilter(filter: any) {
    dispatch(updateFilterSearch(filter));
  },
  updateFilterDialog(dialog: string) {
    dispatch({
      type: UpdateFilterDialog,
      dialog,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterForm);
